import React from "react";
import ScrollModal from "@/components/common/scroll-modal";
import {
  TextField,
  Icon,
  Button,
  Heading,
  GroupTextField,
  GroupTextFieldRow,
  Input,
} from "@elaniintc/goes-design-system";
import { OptionTooltip } from "@/workflow/components/configurations/form/components/OptionTooltip";
import { parseCharacters } from "@/utils/format";
import { useFieldForm } from "@/pages/digital-files/entity/hooks/useFieldForm";
import type {
  EntityFieldType,
  FieldConfig,
} from "@/pages/digital-files/entity/config/type";
import type { EntityField } from "../../types";
import { renderFieldByType } from "./field-renderers";
import { cn } from "@/utils/theme";
import SwitchField from "@/workflow/components/configurations/form/components/SwitchField";
import InfoMessage from "@/workflow/components/configurations/form/components/InfoMessage";
import { Renderify } from "renderify-js";
import { maskitoNumberOptionsGenerator } from "@maskito/kit";
import { useMaskito } from "@maskito/react";

const fieldTypeLabels: Record<string, string> = {
  text: "Texto",
  number: "Números",
  textarea: "Área de texto",
  date: "Fecha y hora",
  date_range: "Rango de fechas",
  select: "Lista de selección",
  email: "Correo electrónico",
  phone: "Número telefónico",
  file: "Subida de archivos",
  identification: "Identificación",
  interoperability: "Interoperabilidad",
};

interface EntityFieldConfigModalProps {
  open: boolean;
  type: string | null;
  sectionId: string | null;
  isCreateMode?: boolean;
  initialField?: EntityField;
  onClose: () => void;
  onSave: (data: EntityField) => void;
}

export const EntityFieldConfigModal: React.FC<EntityFieldConfigModalProps> = ({
  open,
  type,
  sectionId,
  onClose,
  onSave,
  initialField,
  isCreateMode = true,
}) => {
  // Usar el custom hook para manejar el estado del formulario
  const {
    fieldName,
    keyValue,
    formValues,
    localListItems,
    newListItem,
    newFileType,
    locked,
    isValid,
    fieldConfig,
    errors,
    isNew,
    setFieldName,
    setKeyValue,
    updateFormValue,
    setLocalListItems,
    setNewListItem,
    setNewFileType,
    setLocked,
    reset,
    setDataCenterValue,
    getFieldData,
    validate,
    isArray,
    setIsArray,
    allowedQuantityOfValues,
    setAllowedQuantityOfValues,
  } = useFieldForm({
    type: type as EntityFieldType | null,
    initialField,
    isOpen: open,
  });

  const maxNumberOfValuesMask = maskitoNumberOptionsGenerator({
    min: 1,
    minimumFractionDigits: 0,
  });
  const allowedQuantityOfValuesMask = useMaskito({
    options: maxNumberOfValuesMask,
  });

  const handleSave = () => {
    if (!type || !sectionId) return;

    // Validar antes de guardar
    const isValidated = validate();
    if (!isValidated) {
      return;
    }

    const fieldData = getFieldData();
    if (fieldData) {
      onSave(fieldData);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFieldNameChange = (value: string) => {
    setFieldName(value);
    if (!initialField?.id && locked) {
      setKeyValue(parseCharacters(value.toLowerCase()));
    }
  };

  const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyValue(e.target.value);
  };

  const renderField = (fieldConfig: FieldConfig) =>
    renderFieldByType({
      fieldConfig,
      value: formValues[fieldConfig.key],
      formValues,
      updateFormValue,
      localListItems,
      setLocalListItems,
      newListItem,
      setNewListItem,
      newFileType,
      setNewFileType,
      setDataCenterValue,
    });

  return (
    <ScrollModal
      isOpen={open}
      onClose={handleClose}
      modalContentClassName="w-full max-w-[520px] h-full max-h-[680px] flex flex-col"
      header={
        <div className="flex items-center justify-between px-8 py-3">
          <Heading variant="h5" className="mb-0!">
            {type ? fieldTypeLabels[type] || "Campo" : "Modal"}
          </Heading>
          <Button
            type="button"
            onClick={handleClose}
            variant="quiet"
            aria-label="Cerrar"
            className="w-8 h-8 cursor-pointer"
          >
            <Icon type="far" name="times" className="text-sm text-body" />
          </Button>
        </div>
      }
      footer={
        <div className="w-full grid grid-cols-1 tablet:grid-cols-2 py-3 px-8 gap-3">
          <Button
            variant="tertiary"
            size="sm"
            type="button"
            onClick={handleClose}
            className="min-w-30 cursor-pointer"
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            size="sm"
            type="button"
            className="min-w-35 cursor-pointer disabled:bg-neutral-object-accent disabled:text-on-disabled disabled:border-none disabled:shadow-none"
            disabled={!isValid || !type || !sectionId}
            onClick={handleSave}
          >
            Guardar
          </Button>
        </div>
      }
    >
      <div className="px-8.5 space-y-6 overflow-y-auto text-sm">
        {/* Campo especial: Nombre del campo y Key - siempre presente */}
        <Heading variant="h6" className="mb-4">
          Detalles
        </Heading>

        <GroupTextField
          label="Nombre del campo"
          required
          error={errors.fieldName}
        >
          <GroupTextFieldRow>
            <Input
              className="w-full"
              placeholder="Escribe el nombre del campo"
              value={fieldName}
              onChange={(e) => handleFieldNameChange(e.target.value)}
              required
            />
          </GroupTextFieldRow>
          <GroupTextFieldRow>
            <div className="flex items-center w-full relative">
              <Input
                className="w-full pr-20 rounded-br-md!"
                placeholder="Key"
                value={keyValue}
                disabled={locked}
                onChange={handleKeyChange}
                group="down-left"
              />
              <div
                className={cn(
                  "absolute top-0 bottom-0 flex items-center gap-2",
                  isCreateMode || isNew ? "right-0" : "right-3",
                )}
              >
                <OptionTooltip title="La key es el identificador técnico del campo. Se recomienda no modificarla si ya se ha utilizado en integraciones o automatizaciones.">
                  <div className="text-body-quiet cursor-help">
                    <Icon name="circle-info" type="far" />
                  </div>
                </OptionTooltip>
                {(isCreateMode || isNew) && (
                  <Button
                    variant="quiet"
                    type="button"
                    onClick={() => setLocked(!locked)}
                    aria-label={
                      locked
                        ? "Desbloquear edición de key"
                        : "Bloquear edición de key"
                    }
                    className="h-full px-3 bg-neutral-object border-l border-t border-b border-neutral-quiet rounded-br-md rounded-tl-none rounded-tr-none rounded-bl-none flex items-center justify-center"
                  >
                    <Icon
                      name={locked ? "lock" : "lock-open"}
                      type="far"
                      className="text-body-quiet text-sm"
                    />
                  </Button>
                )}
              </div>
            </div>
          </GroupTextFieldRow>
        </GroupTextField>

        {/* Renderizar secciones y campos dinámicamente desde la configuración */}
        {fieldConfig?.sections.map((section, sectionIndex) => {
          // Filtrar campos especiales que ya se muestran arriba
          const filteredFields = section.fields.filter(
            (field) => field.key !== "fieldName",
          );

          // Verificar qué campos deben mostrarse según sus condiciones
          const visibleFields = filteredFields.filter((field) => {
            if (!field.condition) return true;
            return field.condition(formValues);
          });

          // Si no hay campos visibles, no mostrar la sección
          if (visibleFields.length === 0) {
            return null;
          }

          return (
            <React.Fragment key={section.title || `section-${sectionIndex}`}>
              {(section.showSeparator || sectionIndex > 0) &&
                sectionIndex > 0 && (
                  <hr className="border-neutral-quiet my-6" />
                )}
              {sectionIndex > 0 && (
                <Heading variant="h6" className="mb-4">
                  {section.title}
                </Heading>
              )}
              {visibleFields.map((field) => renderField(field))}
            </React.Fragment>
          );
        })}

        {type === "phone" && (
          <>
            <hr className="border-neutral-quiet my-6" />
            <Heading variant="h6" className="mb-4">
              Configuración de múltiples valores
            </Heading>

            <SwitchField
              label="¿Agregar múltiples valores?"
              isTrue="Sí, permitir valores adicionales"
              checked={isArray}
              onChange={(checked) => {
                setIsArray(checked);
                if (!checked) {
                  setAllowedQuantityOfValues("");
                }
              }}
              disabled={!(isCreateMode || isNew)}
              required
            />

            <Renderify condition={isArray}>
              <div>
                <TextField
                  label="Cantidad máxima de items"
                  placeholder="Escribe un número"
                  ref={allowedQuantityOfValuesMask}
                  value={allowedQuantityOfValues}
                  onChange={(e) =>
                    setAllowedQuantityOfValues(e.target.value || "")
                  }
                  disabled={!(isCreateMode || isNew)}
                />
                <InfoMessage>
                  Si se deja vacío, no se aplicará límite de items
                </InfoMessage>
              </div>
            </Renderify>
          </>
        )}
      </div>
    </ScrollModal>
  );
};

export default EntityFieldConfigModal;
